import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingBag,
  Users,
  Star,
  Package,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  Repeat,
  AlertTriangle,
  BarChart3,
  PieChart,
  Target,
  Zap,
  Heart,
  MessageSquare,
  Filter,
  Download,
  RefreshCw,
  Loader2,
  Check,
  X,
} from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import {
  useBusinessAnalytics,
  type KPIResult,
  type DailyRevenue,
  type TopProductResult,
  type HourlyData,
  type CustomerSegment,
  type CategoryRevenue,
  type LowStockItem,
  type OrderStatusGroup,
  type PerformanceMetric,
  type CustomerRow,
  type HeatmapRow,
  type FunnelStep,
  type ReviewItem,
  type TractionData,
  type MostViewedProduct,
} from '../../hooks/useBusinessAnalytics';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { buildCsv, downloadCsvFile, slugifyForFilename, type CsvSection } from '../../lib/csvExport';

// ─── Types ───────────────────────────────────────────────────────────
type DateRange = '7d' | '30d' | '90d' | '12m';
type ActiveSection = 'overview' | 'orders' | 'products' | 'customers' | 'traction';

interface KPICardData extends KPIResult {
  icon: React.ElementType;
  color: string;
}

// ─── KPI icon/color mappings ─────────────────────────────────────────

const OVERVIEW_KPI_META: { icon: React.ElementType; color: string }[] = [
  { icon: DollarSign, color: 'bg-gradient-to-br from-green-400 to-emerald-500' },
  { icon: ShoppingBag, color: 'bg-gradient-to-br from-purple-400 to-indigo-500' },
  { icon: Target, color: 'bg-gradient-to-br from-blue-400 to-cyan-500' },
  { icon: Star, color: 'bg-gradient-to-br from-amber-400 to-orange-500' },
];

const ORDER_KPI_META: { icon: React.ElementType; color: string }[] = [
  { icon: ShoppingBag, color: 'bg-gradient-to-br from-purple-400 to-indigo-500' },
  { icon: Target, color: 'bg-gradient-to-br from-emerald-400 to-green-500' },
  { icon: Clock, color: 'bg-gradient-to-br from-blue-400 to-cyan-500' },
  { icon: Repeat, color: 'bg-gradient-to-br from-amber-400 to-orange-500' },
];

const PRODUCT_KPI_META: { icon: React.ElementType; color: string }[] = [
  { icon: Package, color: 'bg-gradient-to-br from-purple-400 to-indigo-500' },
  { icon: ShoppingBag, color: 'bg-gradient-to-br from-emerald-400 to-green-500' },
  { icon: Target, color: 'bg-gradient-to-br from-blue-400 to-cyan-500' },
  { icon: AlertTriangle, color: 'bg-gradient-to-br from-amber-400 to-orange-500' },
];

const CUSTOMER_KPI_META: { icon: React.ElementType; color: string }[] = [
  { icon: Users, color: 'bg-gradient-to-br from-purple-400 to-indigo-500' },
  { icon: Repeat, color: 'bg-gradient-to-br from-emerald-400 to-green-500' },
  { icon: Star, color: 'bg-gradient-to-br from-amber-400 to-orange-500' },
  { icon: Heart, color: 'bg-gradient-to-br from-pink-400 to-rose-500' },
];

const PERFORMANCE_ICONS: React.ElementType[] = [Zap, Clock, Package, Repeat];
const PERFORMANCE_COLORS = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500'];

function mergeKpiMeta(kpis: KPIResult[], meta: { icon: React.ElementType; color: string }[]): KPICardData[] {
  return kpis.map((kpi, i) => ({
    ...kpi,
    icon: meta[i]?.icon || BarChart3,
    color: meta[i]?.color || 'bg-gradient-to-br from-gray-400 to-gray-500',
  }));
}

// ─── CSV export helpers ──────────────────────────────────────────────

// The heatmap matrix is 12 one-hour buckets starting at 8am.
const HEATMAP_HOUR_LABELS = Array.from({ length: 12 }, (_, i) => `${8 + i}:00`);

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
  '12m': 'Last 12 Months',
};

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function kpiCsvSection(title: string, kpis: KPIResult[]): CsvSection {
  return {
    title,
    headers: ['Metric', 'Value', 'Change %', 'Compared To'],
    rows: kpis.map((kpi) => [kpi.label, kpi.value, kpi.change, kpi.changeLabel]),
  };
}

// ─── Sub-components ──────────────────────────────────────────────────

function MiniSparkline({ data, color = 'text-primary' }: { data: number[]; color?: string }) {
  if (!data.length || data.every(v => v === 0)) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 80;
  const height = 28;
  const padding = 2;

  const points = data
    .map((val, i) => {
      const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((val - min) / range) * (height - 2 * padding);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={width} height={height} className={cn('flex-shrink-0', color)}>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KPICard({ kpi }: { kpi: KPICardData }) {
  const Icon = kpi.icon;
  const isPositive = kpi.change >= 0;

  return (
    <Card className="relative overflow-hidden group hover:shadow-md transition-shadow">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-body truncate">{kpi.label}</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-heading mt-1.5">
              {kpi.value}
            </p>
            <div className="flex items-center mt-2 space-x-1">
              {kpi.change !== 0 && (
                <>
                  {isPositive ? (
                    <ArrowUpRight className="h-3.5 w-3.5 text-success flex-shrink-0" />
                  ) : (
                    <ArrowDownRight className="h-3.5 w-3.5 text-error flex-shrink-0" />
                  )}
                  <span
                    className={cn(
                      'text-xs sm:text-sm font-semibold',
                      isPositive ? 'text-success' : 'text-error'
                    )}
                  >
                    {isPositive ? '+' : ''}
                    {kpi.change}%
                  </span>
                </>
              )}
              <span className="text-xs text-body hidden sm:inline">{kpi.changeLabel}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={cn('p-2.5 rounded-xl flex-shrink-0', kpi.color)}>
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <MiniSparkline
              data={kpi.sparkline}
              color={isPositive ? 'text-success' : 'text-error'}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RevenueChart({ data }: { data: DailyRevenue[] }) {
  const maxRevenue = Math.max(...data.map((d) => d.revenue), 1);
  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0);
  const totalOrders = data.reduce((sum, d) => sum + d.orders, 0);
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 p-4 sm:p-5">
        <div>
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span>Revenue Overview</span>
          </CardTitle>
          <div className="flex items-center gap-4 mt-2">
            <p className="text-xs text-body">
              Total: <span className="font-semibold text-heading">${totalRevenue.toLocaleString()}</span>
            </p>
            <p className="text-xs text-body">
              Orders: <span className="font-semibold text-heading">{totalOrders}</span>
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-[10px] text-body w-10">
            <span>${(maxRevenue / 1000).toFixed(1)}k</span>
            <span>${((maxRevenue * 0.5) / 1000).toFixed(1)}k</span>
            <span>$0</span>
          </div>
          <div className="ml-12">
            <div className="absolute left-12 right-0 top-0 bottom-8">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-t border-dashed border-gray-100"
                  style={{ top: `${i * 50}%` }}
                />
              ))}
            </div>
            <div className="flex items-end justify-around gap-1 sm:gap-2 h-48 sm:h-56 relative">
              {data.map((d, i) => {
                const height = (d.revenue / maxRevenue) * 100;
                const isHovered = hoveredBar === i;
                return (
                  <div
                    key={i}
                    className="flex-1 flex flex-col items-center justify-end relative group cursor-pointer"
                    onMouseEnter={() => setHoveredBar(i)}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    {isHovered && (
                      <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-heading text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap z-10 shadow-lg">
                        <p className="font-semibold">${d.revenue.toLocaleString()}</p>
                        <p className="text-white/70">{d.orders} orders</p>
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-heading" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'w-full max-w-[48px] rounded-t-lg transition-all duration-300',
                        isHovered
                          ? 'bg-gradient-to-t from-purple-600 to-purple-400 shadow-lg shadow-purple-200'
                          : 'bg-gradient-to-t from-purple-500/80 to-purple-300/60'
                      )}
                      style={{ height: `${height}%`, minHeight: '4px' }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-around mt-2">
              {data.map((d, i) => (
                <span
                  key={i}
                  className={cn(
                    'text-[10px] sm:text-xs flex-1 text-center',
                    hoveredBar === i ? 'text-heading font-semibold' : 'text-body'
                  )}
                >
                  {d.day}
                </span>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OrderFunnelCard({ funnel }: { funnel: FunnelStep[] }) {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Conversion Funnel</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="space-y-3">
          {funnel.map((step, i) => {
            const widthPercent = funnel[0].count > 0
              ? 40 + (60 * step.count) / funnel[0].count
              : 40;
            const isLast = i === funnel.length - 1;
            const dropOff =
              i > 0 && funnel[i - 1].count > 0
                ? (
                    ((funnel[i - 1].count - step.count) / funnel[i - 1].count) *
                    100
                  ).toFixed(1)
                : null;

            return (
              <div key={step.stage} className="relative">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs sm:text-sm font-medium text-heading">{step.stage}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-bold text-heading">
                      {step.count.toLocaleString()}
                    </span>
                    {dropOff && (
                      <span className="text-[10px] text-error font-medium">-{dropOff}%</span>
                    )}
                  </div>
                </div>
                <div className="h-7 sm:h-8 bg-gray-50 rounded-lg overflow-hidden relative">
                  <div
                    className={cn(
                      'h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2',
                      isLast
                        ? 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                        : 'bg-gradient-to-r from-purple-300/50 to-purple-400/60'
                    )}
                    style={{ width: `${widthPercent}%` }}
                  >
                    <span className="text-[10px] font-semibold text-white/90">
                      {step.percentage}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function TopProductsCard({ products }: { products: TopProductResult[] }) {
  if (!products.length) {
    return (
      <Card>
        <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span>Top Products</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <p className="text-sm text-body">No product sales data yet for this period.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Top Products</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-50">
          {products.map((product, idx) => (
            <div
              key={product.id}
              className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-gray-50/50 transition-colors"
            >
              <span
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0',
                  idx === 0
                    ? 'bg-amber-100 text-amber-700'
                    : idx === 1
                      ? 'bg-gray-200 text-gray-600'
                      : idx === 2
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-gray-100 text-gray-500'
                )}
              >
                {idx + 1}
              </span>
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Package className="h-5 w-5 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading truncate">{product.name}</p>
                <p className="text-xs text-body">{product.category}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-heading">
                  ${product.revenue.toLocaleString()}
                </p>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                  <span className="text-xs text-body">{product.unitsSold} sold</span>
                  {product.trend !== 0 && (
                    <span
                      className={cn(
                        'text-[10px] font-semibold',
                        product.trend >= 0 ? 'text-success' : 'text-error'
                      )}
                    >
                      {product.trend >= 0 ? '+' : ''}
                      {product.trend}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PeakHoursCard({ data }: { data: HourlyData[] }) {
  const maxOrders = Math.max(...data.map((d) => d.orders), 1);
  const totalOrders = data.reduce((s, d) => s + d.orders, 0);

  // Find peak 2-hour window
  let peakStart = 8;
  let peakMax = 0;
  for (let i = 0; i < data.length - 1; i++) {
    const windowTotal = data[i].orders + (data[i + 1]?.orders || 0);
    if (windowTotal > peakMax) {
      peakMax = windowTotal;
      peakStart = data[i].hour;
    }
  }
  const formatHourLabel = (h: number) => {
    if (h === 0 || h === 12) return h === 0 ? '12:00 AM' : '12:00 PM';
    return h > 12 ? `${h - 12}:00 PM` : `${h}:00 AM`;
  };

  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Peak Hours</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-end justify-between gap-1 h-32 sm:h-40">
          {data.map((d) => {
            const height = (d.orders / maxOrders) * 100;
            const isPeak = d.orders >= maxOrders * 0.8;
            const formatHour = (h: number) => {
              if (h === 0 || h === 12) return h === 0 ? '12a' : '12p';
              return h > 12 ? `${h - 12}p` : `${h}a`;
            };

            return (
              <div key={d.hour} className="flex-1 flex flex-col items-center gap-1 group">
                <span className="text-[9px] text-body opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                  {d.orders}
                </span>
                <div
                  className={cn(
                    'w-full max-w-[28px] rounded-t-md transition-all duration-300 group-hover:opacity-100',
                    isPeak
                      ? 'bg-gradient-to-t from-purple-600 to-purple-400'
                      : 'bg-gradient-to-t from-purple-300/50 to-purple-200/40'
                  )}
                  style={{ height: `${height}%`, minHeight: '3px' }}
                />
                <span className="text-[9px] sm:text-[10px] text-body">{formatHour(d.hour)}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-body">Peak Time</p>
              <p className="text-sm font-semibold text-heading">
                {formatHourLabel(peakStart)} - {formatHourLabel(peakStart + 2)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-body">Avg. Orders/Hour</p>
              <p className="text-sm font-semibold text-heading">
                {data.length > 0 ? (totalOrders / data.length).toFixed(1) : '0'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerInsightsCard({
  segments,
  totalCustomers,
  retentionRate,
  avgLtv,
}: {
  segments: CustomerSegment[];
  totalCustomers: number;
  retentionRate: number;
  avgLtv: number;
}) {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Customer Insights</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-center gap-6 mb-5">
          <div className="relative w-28 h-28 sm:w-32 sm:h-32">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              {(() => {
                let offset = 0;
                return segments.map((seg) => {
                  const dashArray = (seg.percentage / 100) * 283;
                  const dashOffset = -offset;
                  offset += dashArray;
                  const colorMap: Record<string, string> = {
                    'bg-purple-500': '#a855f7',
                    'bg-blue-500': '#3b82f6',
                    'bg-emerald-500': '#10b981',
                  };
                  return (
                    <circle
                      key={seg.label}
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke={colorMap[seg.color] || '#a855f7'}
                      strokeWidth="10"
                      strokeDasharray={`${dashArray} ${283 - dashArray}`}
                      strokeDashoffset={dashOffset}
                      className="transition-all duration-500"
                    />
                  );
                });
              })()}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl sm:text-2xl font-bold text-heading">{totalCustomers}</span>
              <span className="text-[10px] text-body">Customers</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {segments.map((seg) => (
            <div key={seg.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={cn('w-3 h-3 rounded-full', seg.color)} />
                <span className="text-xs sm:text-sm text-heading">{seg.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm font-semibold text-heading">{seg.count}</span>
                <span className="text-[10px] text-body">({seg.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-body">Retention Rate</p>
            <p className="text-lg font-bold text-heading">{retentionRate}%</p>
          </div>
          <div>
            <p className="text-xs text-body">Avg. Lifetime Value</p>
            <p className="text-lg font-bold text-heading">{avgLtv > 0 ? `$${avgLtv}` : 'N/A'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryBreakdownCard({ categories }: { categories: CategoryRevenue[] }) {
  const maxRevenue = Math.max(...categories.map((c) => c.revenue), 1);

  if (!categories.length) {
    return (
      <Card>
        <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <PieChart className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span>Revenue by Category</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <p className="text-sm text-body">No category data yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <PieChart className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Revenue by Category</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="space-y-3">
          {categories.map((cat) => (
            <div key={cat.name}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs sm:text-sm font-medium text-heading">{cat.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm font-bold text-heading">
                    ${cat.revenue.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-body">{cat.percentage}%</span>
                </div>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full bg-gradient-to-r transition-all duration-500',
                    cat.color
                  )}
                  style={{ width: `${(cat.revenue / maxRevenue) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function WeeklyHeatmapCard({ heatmap }: { heatmap: HeatmapRow[] }) {
  const allValues = heatmap.flatMap((d) => d.hours);
  const maxVal = Math.max(...allValues, 1);
  const timeLabels = ['8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p', '7p'];

  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Weekly Activity Heatmap</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="overflow-x-auto">
          <div className="min-w-[380px]">
            <div className="flex mb-1 ml-10">
              {timeLabels.map((t, i) => (
                <span key={i} className="flex-1 text-[9px] text-body text-center">
                  {t}
                </span>
              ))}
            </div>
            <div className="space-y-1">
              {heatmap.map((day) => (
                <div key={day.day} className="flex items-center gap-1">
                  <span className="w-8 text-[10px] text-body text-right flex-shrink-0">
                    {day.day}
                  </span>
                  <div className="flex gap-0.5 flex-1">
                    {day.hours.map((val, i) => {
                      const intensity = val / maxVal;
                      return (
                        <div
                          key={i}
                          className="flex-1 h-5 sm:h-6 rounded-sm transition-all duration-200 hover:ring-2 hover:ring-purple-300 cursor-pointer"
                          style={{
                            backgroundColor:
                              intensity < 0.1
                                ? '#f3f4f6'
                                : intensity < 0.3
                                  ? '#e9d5ff'
                                  : intensity < 0.5
                                    ? '#c084fc'
                                    : intensity < 0.7
                                      ? '#a855f7'
                                      : intensity < 0.85
                                        ? '#9333ea'
                                        : '#7c3aed',
                          }}
                          title={`${val} orders`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-1 mt-3">
              <span className="text-[9px] text-body mr-1">Less</span>
              {['#f3f4f6', '#e9d5ff', '#c084fc', '#a855f7', '#9333ea', '#7c3aed'].map(
                (color, i) => (
                  <div
                    key={i}
                    className="w-4 h-4 rounded-sm"
                    style={{ backgroundColor: color }}
                  />
                )
              )}
              <span className="text-[9px] text-body ml-1">More</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function LowStockCard({ items }: { items: LowStockItem[] }) {
  if (!items.length) {
    return (
      <Card className="border-l-4 border-l-emerald-400">
        <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <Package className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-500" />
            <span>Stock Status</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <p className="text-sm text-body">All products are well-stocked.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-amber-400">
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
          <span>Low Stock Alerts</span>
          <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto">
            {items.length}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-50">
          {items.map((item, i) => (
            <div
              key={`${item.name}-${item.size}-${i}`}
              className="flex items-center gap-3 p-3 sm:p-4 hover:bg-amber-50/30 transition-colors"
            >
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Package className="h-5 w-5 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-heading truncate">{item.name}</p>
                <p className="text-xs text-body">Size: {item.size}</p>
              </div>
              <div
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-bold',
                  item.remaining <= 1
                    ? 'bg-red-100 text-red-700'
                    : item.remaining <= 2
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-yellow-100 text-yellow-700'
                )}
              >
                {item.remaining} left
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RecentReviewsCard({ reviews }: { reviews: ReviewItem[] }) {
  if (!reviews.length) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 p-4 sm:p-5">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span>Recent Reviews</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-heading">No reviews yet</p>
          <p className="text-xs text-body mt-1">Customer reviews will appear here once shoppers leave feedback.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Recent Reviews</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-50">
          {reviews.map((review) => (
            <div key={review.id} className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                    {review.customer.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-heading">{review.customer}</p>
                    {review.product && (
                      <p className="text-[10px] text-body">{review.product}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={cn(
                          'h-3 w-3',
                          i < review.rating
                            ? 'text-amber-400 fill-amber-400'
                            : 'text-gray-200 fill-gray-200'
                        )}
                      />
                    ))}
                  </div>
                  {review.date && (
                    <span className="text-[10px] text-body ml-1">{review.date}</span>
                  )}
                </div>
              </div>
              {review.text && (
                <p className="text-xs text-body leading-relaxed line-clamp-2">{review.text}</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PerformanceMetricsCard({ metrics }: { metrics: PerformanceMetric[] }) {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Target className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Performance Scorecard</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {metrics.map((metric, idx) => {
            const Icon = PERFORMANCE_ICONS[idx] || Target;
            const color = PERFORMANCE_COLORS[idx] || 'bg-gray-500';
            return (
              <div
                key={metric.label}
                className="p-3 rounded-xl bg-gray-50/80 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-body" />
                    <span className="text-xs text-body">{metric.label}</span>
                  </div>
                  {metric.achieved && (
                    <span className="text-[10px] text-success font-medium bg-success/10 px-1.5 py-0.5 rounded-full">
                      On Target
                    </span>
                  )}
                </div>
                <div className="flex items-end justify-between mb-2">
                  <span className="text-lg font-bold text-heading">{metric.value}</span>
                  <span className="text-[10px] text-body">Target: {metric.target}</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all duration-700', color)}
                    style={{ width: `${Math.min(metric.barWidth, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function OrderStatusBreakdownCard({
  statusGroups,
  avgOrderValue,
  avgItemsPerOrder,
  avgFulfillmentTime,
}: {
  statusGroups: OrderStatusGroup[];
  avgOrderValue: number;
  avgItemsPerOrder: number;
  avgFulfillmentTime: number;
}) {
  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Order Status Breakdown</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="space-y-4">
          {statusGroups.map((status) => (
            <div key={status.label}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <div className={cn('w-2.5 h-2.5 rounded-full', status.color)} />
                  <span className="text-xs sm:text-sm text-heading">{status.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm font-bold text-heading">
                    {status.count}
                  </span>
                  <span className="text-[10px] text-body">
                    ({status.total > 0 ? ((status.count / status.total) * 100).toFixed(1) : '0'}%)
                  </span>
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    status.color
                  )}
                  style={{ width: `${status.total > 0 ? (status.count / status.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-bold text-heading">
              ${avgOrderValue > 0 ? avgOrderValue.toFixed(0) : '0'}
            </p>
            <p className="text-[10px] text-body">Avg. Order Value</p>
          </div>
          <div>
            <p className="text-lg font-bold text-heading">
              {avgItemsPerOrder > 0 ? avgItemsPerOrder.toFixed(1) : '0'}
            </p>
            <p className="text-[10px] text-body">Items per Order</p>
          </div>
          <div>
            <p className="text-lg font-bold text-heading">
              {avgFulfillmentTime > 0 ? `${avgFulfillmentTime}min` : 'N/A'}
            </p>
            <p className="text-[10px] text-body">Avg. Fulfillment</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TopCustomersTable({ customers }: { customers: CustomerRow[] }) {
  if (!customers.length) {
    return (
      <Card>
        <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <Star className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span>Top Customers</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <p className="text-sm text-body">No customer data yet for this period.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Star className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Top Customers</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-medium text-body py-3 px-4">Customer</th>
                <th className="text-left text-xs font-medium text-body py-3 px-4 hidden sm:table-cell">Orders</th>
                <th className="text-left text-xs font-medium text-body py-3 px-4">Total Spent</th>
                <th className="text-left text-xs font-medium text-body py-3 px-4 hidden md:table-cell">Last Order</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {customers.map((customer, i) => (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {customer.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .slice(0, 2)}
                      </div>
                      <span className="text-sm font-medium text-heading">
                        {customer.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 hidden sm:table-cell">
                    <span className="text-sm text-heading">{customer.orders}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm font-semibold text-heading">
                      ${customer.spent.toLocaleString()}
                    </span>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <span className="text-xs text-body">{customer.lastOrder}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductPerformanceCard({ products }: { products: TopProductResult[] }) {
  if (!products.length) {
    return (
      <Card>
        <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
          <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
            <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span>Product Performance</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-8 text-center">
          <p className="text-sm text-body">No performance data yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
        <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
          <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          <span>Product Performance</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 sm:p-5">
        <div className="space-y-4">
          {products.slice(0, 4).map((product) => (
            <div key={product.id} className="p-3 bg-gray-50/80 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-heading truncate">
                  {product.name}
                </span>
                <span className="text-xs text-body">{product.conversionRate}% CVR</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full"
                      style={{
                        width: `${Math.min((product.conversionRate / 15) * 100, 100)}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {product.trend >= 0 ? (
                    <TrendingUp className="h-3 w-3 text-success" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-error" />
                  )}
                  <span
                    className={cn(
                      'text-xs font-semibold',
                      product.trend >= 0 ? 'text-success' : 'text-error'
                    )}
                  >
                    {product.trend >= 0 ? '+' : ''}
                    {product.trend}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Dashboard Component ────────────────────────────────────────

export function BusinessAnalyticsDashboard() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [activeSection, setActiveSection] = useState<ActiveSection>('overview');
  const [businessName, setBusinessName] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isAdmin = userProfile?.role === 'admin';
  const businessId = isAdmin ? undefined : user?.uid;

  // Fetch real business name
  useEffect(() => {
    if (!user) return;
    if (isAdmin) {
      setBusinessName('Platform Overview');
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, 'businesses', user.uid),
      (snap) => {
        if (snap.exists()) {
          setBusinessName(snap.data().name || 'Your Boutique');
        }
      }
    );
    return () => unsubscribe();
  }, [user, isAdmin]);

  const {
    loading,
    error,
    kpis,
    orderKpis,
    productKpis,
    customerKpis,
    revenueChart,
    topProducts,
    categoryBreakdown,
    peakHours,
    weeklyHeatmap,
    orderStatusBreakdown,
    lowStockItems,
    customerSegments,
    totalCustomers,
    retentionRate,
    avgLtv,
    topCustomers,
    performance,
    funnel,
    avgOrderValue,
    avgItemsPerOrder,
    avgFulfillmentTime,
    traction,
    reviews,
  } = useBusinessAnalytics(businessId, dateRange);

  const dateRangeOptions: { value: DateRange; label: string }[] = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '12m', label: 'Last 12 Months' },
  ];

  const sections: { id: ActiveSection; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'traction', label: 'Traction', icon: Eye },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'customers', label: 'Customers', icon: Users },
  ];

  const overviewKpiCards = mergeKpiMeta(kpis, OVERVIEW_KPI_META);
  const orderKpiCards = mergeKpiMeta(orderKpis, ORDER_KPI_META);
  const productKpiCards = mergeKpiMeta(productKpis, PRODUCT_KPI_META);
  const customerKpiCards = mergeKpiMeta(customerKpis, CUSTOMER_KPI_META);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const buildExportSections = (): CsvSection[] => {
    switch (activeSection) {
      case 'traction': {
        const today = new Date();
        return [
          {
            title: 'Traction Summary',
            headers: ['Metric', 'Value'],
            rows: [
              ['Store Visits', traction.storeVisits],
              ['Unique Visitors', traction.uniqueVisitors],
              ['Avg Session Duration (seconds)', traction.avgSessionDurationSec],
            ],
          },
          {
            title: 'Store Visits by Day',
            headers: ['Date', 'Visits'],
            rows: traction.visitsByDay.map((visits, i) => {
              const day = new Date(today);
              day.setDate(day.getDate() - (traction.visitsByDay.length - 1 - i));
              return [isoDate(day), visits];
            }),
          },
          {
            title: 'Most Viewed Products',
            headers: ['Product', 'Views'],
            rows: traction.mostViewedProducts.map((p) => [p.name, p.views]),
          },
          {
            title: 'Conversion Funnel',
            headers: ['Stage', 'Count', 'Share %'],
            rows: funnel.map((step) => [step.stage, step.count, step.percentage]),
          },
        ];
      }

      case 'orders':
        return [
          kpiCsvSection('Order KPIs', orderKpis),
          {
            title: 'Order Averages',
            headers: ['Metric', 'Value'],
            rows: [
              ['Average Order Value', avgOrderValue],
              ['Average Items per Order', avgItemsPerOrder],
              ['Average Fulfillment Time (minutes)', avgFulfillmentTime],
            ],
          },
          {
            title: 'Order Status Breakdown',
            headers: ['Status', 'Orders', 'Total Orders'],
            rows: orderStatusBreakdown.map((s) => [s.label, s.count, s.total]),
          },
          {
            title: 'Orders by Hour',
            headers: ['Hour', 'Orders'],
            rows: peakHours.map((h) => [`${h.hour}:00`, h.orders]),
          },
          {
            title: 'Orders by Day and Hour',
            headers: ['Day', ...HEATMAP_HOUR_LABELS],
            rows: weeklyHeatmap.map((row) => [row.day, ...row.hours]),
          },
          {
            title: 'Performance vs Target',
            headers: ['Metric', 'Value', 'Target', 'Target Met'],
            rows: performance.map((m) => [m.label, m.value, m.target, m.achieved ? 'Yes' : 'No']),
          },
        ];

      case 'products':
        return [
          kpiCsvSection('Product KPIs', productKpis),
          {
            title: 'Top Products',
            headers: ['Product', 'Category', 'Units Sold', 'Revenue', 'Trend %', 'Conversion Rate %'],
            rows: topProducts.map((p) => [
              p.name,
              p.category,
              p.unitsSold,
              p.revenue,
              p.trend,
              p.conversionRate,
            ]),
          },
          {
            title: 'Revenue by Category',
            headers: ['Category', 'Revenue', 'Share %'],
            rows: categoryBreakdown.map((c) => [c.name, c.revenue, c.percentage]),
          },
          {
            title: 'Low Stock Items',
            headers: ['Product', 'Size', 'Remaining'],
            rows: lowStockItems.map((item) => [item.name, item.size, item.remaining]),
          },
        ];

      case 'customers':
        return [
          kpiCsvSection('Customer KPIs', customerKpis),
          {
            title: 'Customer Summary',
            headers: ['Metric', 'Value'],
            rows: [
              ['Total Customers', totalCustomers],
              ['Retention Rate %', retentionRate],
              ['Average Lifetime Value', avgLtv],
            ],
          },
          {
            title: 'Customer Segments',
            headers: ['Segment', 'Customers', 'Share %'],
            rows: customerSegments.map((s) => [s.label, s.count, s.percentage]),
          },
          {
            title: 'Top Customers',
            headers: ['Customer', 'Orders', 'Total Spent', 'Last Order'],
            rows: topCustomers.map((c) => [c.name, c.orders, c.spent, c.lastOrder]),
          },
        ];

      case 'overview':
      default:
        return [
          kpiCsvSection('Overview KPIs', kpis),
          {
            title: 'Revenue Trend',
            headers: ['Period', 'Revenue', 'Orders'],
            rows: revenueChart.map((d) => [d.day, d.revenue, d.orders]),
          },
          {
            title: 'Conversion Funnel',
            headers: ['Stage', 'Count', 'Share %'],
            rows: funnel.map((step) => [step.stage, step.count, step.percentage]),
          },
          {
            title: 'Performance vs Target',
            headers: ['Metric', 'Value', 'Target', 'Target Met'],
            rows: performance.map((m) => [m.label, m.value, m.target, m.achieved ? 'Yes' : 'No']),
          },
          {
            title: 'Orders by Day and Hour',
            headers: ['Day', ...HEATMAP_HOUR_LABELS],
            rows: weeklyHeatmap.map((row) => [row.day, ...row.hours]),
          },
          {
            title: 'Low Stock Items',
            headers: ['Product', 'Size', 'Remaining'],
            rows: lowStockItems.map((item) => [item.name, item.size, item.remaining]),
          },
          {
            title: 'Recent Reviews',
            headers: ['Date', 'Customer', 'Rating', 'Product', 'Review'],
            rows: reviews.map((r) => [r.date, r.customer, r.rating, r.product, r.text]),
          },
        ];
    }
  };

  const handleExport = () => {
    const sectionLabel = sections.find((s) => s.id === activeSection)?.label || 'Analytics';
    const dataSections = buildExportSections();

    try {
      const csv = buildCsv([
        {
          title: `${businessName || 'Boutique'} — ${sectionLabel} Report`,
          headers: ['Field', 'Value'],
          rows: [
            ['Store', businessName || 'Boutique'],
            ['Report', sectionLabel],
            ['Date Range', DATE_RANGE_LABELS[dateRange]],
            ['Generated', new Date().toLocaleString()],
          ],
        },
        ...dataSections,
      ]);

      const filename = [
        slugifyForFilename(businessName || 'boutique', 'boutique'),
        slugifyForFilename(sectionLabel, 'analytics'),
        dateRange,
        isoDate(new Date()),
      ].join('-');

      downloadCsvFile(`${filename}.csv`, csv);

      const hasRows = dataSections.some((s) => s.rows.length > 0);
      showToast(
        'success',
        hasRows
          ? `${sectionLabel} report exported`
          : `${sectionLabel} report exported — no data for this period yet`
      );
    } catch (err) {
      console.error('Analytics export failed:', err);
      showToast('error', 'Could not create the export file. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {toastMessage && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full px-4">
          <div
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-2xl shadow-lg border backdrop-blur-sm',
              toastMessage.type === 'success'
                ? 'bg-green-50/95 border-green-200 text-green-800'
                : 'bg-red-50/95 border-red-200 text-red-800'
            )}
          >
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                toastMessage.type === 'success' ? 'bg-green-100' : 'bg-red-100'
              )}
            >
              {toastMessage.type === 'success' ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-500" />
              )}
            </div>
            <p className="text-sm font-medium flex-1">{toastMessage.text}</p>
            <button
              onClick={() => setToastMessage(null)}
              className={cn(
                'p-1 rounded-full transition-colors flex-shrink-0',
                toastMessage.type === 'success' ? 'hover:bg-green-100' : 'hover:bg-red-100'
              )}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/business')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-heading" />
              </button>
              <div>
                <h1 className="text-xl font-serif text-heading">Analytics</h1>
                <p className="text-xs text-body">{businessName || 'Loading...'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button variant="ghost" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Admin notice */}
        {isAdmin && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Platform-wide analytics require server-side aggregation. Currently showing data for individual business accounts only.
          </div>
        )}

        {/* Controls Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex space-x-1 bg-white rounded-xl p-1 overflow-x-auto shadow-soft border border-gray-100">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap',
                    activeSection === section.id
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-body hover:bg-gray-50'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1 bg-white rounded-xl p-1 shadow-soft border border-gray-100">
            {dateRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setDateRange(option.value)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap',
                  dateRange === option.value
                    ? 'bg-heading text-white'
                    : 'text-body hover:bg-gray-50'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-body">Loading analytics...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 mb-6">
            Error loading analytics: {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ─── OVERVIEW SECTION ─── */}
            {activeSection === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {overviewKpiCards.map((kpi) => (
                    <KPICard key={kpi.label} kpi={kpi} />
                  ))}
                </div>
                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <RevenueChart data={revenueChart} />
                  </div>
                  <OrderFunnelCard funnel={funnel} />
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                  <PerformanceMetricsCard metrics={performance} />
                  <WeeklyHeatmapCard heatmap={weeklyHeatmap} />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <LowStockCard items={lowStockItems} />
                  <RecentReviewsCard reviews={reviews} />
                </div>
              </div>
            )}

            {/* ─── TRACTION SECTION ─── */}
            {activeSection === 'traction' && (
              <div className="space-y-6">
                {/* Traction KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <Card className="relative overflow-hidden">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-body">Store Visits</p>
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-heading mt-1.5">
                            {traction.storeVisits.toLocaleString()}
                          </p>
                          <p className="text-xs text-body mt-2">Total page views</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex-shrink-0">
                          <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="relative overflow-hidden">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-body">Unique Visitors</p>
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-heading mt-1.5">
                            {traction.uniqueVisitors.toLocaleString()}
                          </p>
                          <p className="text-xs text-body mt-2">Signed-in shoppers</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex-shrink-0">
                          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="relative overflow-hidden">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-body">Avg. Time on Store</p>
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-heading mt-1.5">
                            {traction.avgSessionDurationSec > 0
                              ? `${Math.floor(traction.avgSessionDurationSec / 60)}m ${traction.avgSessionDurationSec % 60}s`
                              : 'N/A'}
                          </p>
                          <p className="text-xs text-body mt-2">Per session</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex-shrink-0">
                          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="relative overflow-hidden">
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-body">Browse-to-Buy</p>
                          <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-heading mt-1.5">
                            {traction.storeVisits > 0
                              ? `${((funnel[funnel.length - 1]?.count || 0) / traction.storeVisits * 100).toFixed(1)}%`
                              : 'N/A'}
                          </p>
                          <p className="text-xs text-body mt-2">Conversion rate</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex-shrink-0">
                          <Target className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Visits trend + Most viewed products */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Visit Trend Chart */}
                  <Card>
                    <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
                      <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                        <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                        <span>Store Visits (Last 7 Days)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-end gap-1.5 h-32">
                        {traction.visitsByDay.map((count, i) => {
                          const max = Math.max(...traction.visitsByDay, 1);
                          const height = (count / max) * 100;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-xs text-body font-medium">{count}</span>
                              <div
                                className="w-full bg-gradient-to-t from-primary/80 to-primary rounded-t-md transition-all"
                                style={{ height: `${Math.max(height, 4)}%` }}
                              />
                              <span className="text-[10px] text-body">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Most Viewed Products */}
                  <Card>
                    <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
                      <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                        <span>Most Viewed Items</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-5">
                      {traction.mostViewedProducts.length === 0 ? (
                        <p className="text-sm text-body text-center py-8">No product views recorded yet</p>
                      ) : (
                        <div className="space-y-3">
                          {traction.mostViewedProducts.map((product, i) => {
                            const maxViews = traction.mostViewedProducts[0]?.views || 1;
                            return (
                              <div key={product.id} className="flex items-center gap-3">
                                <span className="text-xs font-bold text-body w-5">{i + 1}</span>
                                {product.image ? (
                                  <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-9 h-9 rounded-lg object-cover"
                                  />
                                ) : (
                                  <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                                    <Package className="h-4 w-4 text-gray-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-heading truncate">{product.name}</p>
                                  <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full"
                                      style={{ width: `${(product.views / maxViews) * 100}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="text-sm font-semibold text-heading">{product.views} views</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Customer Engagement Insights */}
                <Card>
                  <CardHeader className="border-b border-gray-100 p-4 sm:p-5">
                    <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                      <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                      <span>Visitor Insights</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-5">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="text-center p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50">
                        <p className="text-2xl font-bold text-heading">
                          {traction.storeVisits > 0 && traction.uniqueVisitors > 0
                            ? (traction.storeVisits / traction.uniqueVisitors).toFixed(1)
                            : '0'}
                        </p>
                        <p className="text-xs text-body mt-1">Visits per Customer</p>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-gradient-to-br from-purple-50 to-violet-50">
                        <p className="text-2xl font-bold text-heading">
                          {traction.mostViewedProducts.reduce((s, p) => s + p.views, 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-body mt-1">Total Product Views</p>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50">
                        <p className="text-2xl font-bold text-heading">
                          {traction.avgSessionDurationSec > 0
                            ? `${Math.floor(traction.avgSessionDurationSec / 60)}:${String(traction.avgSessionDurationSec % 60).padStart(2, '0')}`
                            : '0:00'}
                        </p>
                        <p className="text-xs text-body mt-1">Avg. Browse Duration</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ─── ORDERS SECTION ─── */}
            {activeSection === 'orders' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {orderKpiCards.map((kpi) => (
                    <KPICard key={kpi.label} kpi={kpi} />
                  ))}
                </div>
                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <RevenueChart data={revenueChart} />
                  </div>
                  <OrderFunnelCard funnel={funnel} />
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                  <PeakHoursCard data={peakHours} />
                  <OrderStatusBreakdownCard
                    statusGroups={orderStatusBreakdown}
                    avgOrderValue={avgOrderValue}
                    avgItemsPerOrder={avgItemsPerOrder}
                    avgFulfillmentTime={avgFulfillmentTime}
                  />
                </div>
                <WeeklyHeatmapCard heatmap={weeklyHeatmap} />
              </div>
            )}

            {/* ─── PRODUCTS SECTION ─── */}
            {activeSection === 'products' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {productKpiCards.map((kpi) => (
                    <KPICard key={kpi.label} kpi={kpi} />
                  ))}
                </div>
                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <TopProductsCard products={topProducts} />
                  </div>
                  <CategoryBreakdownCard categories={categoryBreakdown} />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <LowStockCard items={lowStockItems} />
                  <ProductPerformanceCard products={topProducts} />
                </div>
              </div>
            )}

            {/* ─── CUSTOMERS SECTION ─── */}
            {activeSection === 'customers' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {customerKpiCards.map((kpi) => (
                    <KPICard key={kpi.label} kpi={kpi} />
                  ))}
                </div>
                <div className="grid lg:grid-cols-3 gap-6">
                  <CustomerInsightsCard
                    segments={customerSegments}
                    totalCustomers={totalCustomers}
                    retentionRate={retentionRate}
                    avgLtv={avgLtv}
                  />
                  <div className="lg:col-span-2">
                    <RecentReviewsCard reviews={reviews} />
                  </div>
                </div>
                <TopCustomersTable customers={topCustomers} />
                <WeeklyHeatmapCard heatmap={weeklyHeatmap} />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
